import React, { useRef, useState, useEffect } from 'react';
import { Upload, Plus, Trash2 } from 'lucide-react';

// Empty string -> all fetches go same-origin (e.g. /api/...).
// This avoids baking a hostname into the build, which breaks if the site
// is later served from a different domain.
export const API_URL = '';

export const getToken = () => localStorage.getItem('adminToken');

export const apiCall = async (method, endpoint, body = null) => {
  const token = getToken();
  const options = {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  if (body) options.body = JSON.stringify(body);
  const response = await fetch(`${API_URL}/api/admin/${endpoint}`, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'Request failed');
  return data;
};

export const fetchAdmin = async (endpoint) => {
  const token = getToken();
  const res = await fetch(`${API_URL}/api/admin/${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return res.json();
};

export const useAdminData = (endpoint, defaultValue = []) => {
  const [data, setData] = useState(defaultValue);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    fetchAdmin(endpoint).then((d) => {
      if (d != null) setData(Array.isArray(defaultValue) ? (Array.isArray(d) ? d : []) : (d || defaultValue));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, version]);
  const refetch = () => setVersion((v) => v + 1);
  return [data, setData, refetch];
};

export const ImageUploader = ({ onUpload, currentImage, label = 'Upload Image' }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || '');

  useEffect(() => {
    setPreview(currentImage || '');
  }, [currentImage]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      // Always store the relative path. Same-origin in prod, and the public
      // /api/* proxy routes to the backend in dev. Storing an absolute URL
      // bakes in whatever host was active at upload time and breaks if the
      // site is later served from a different domain.
      const relativeUrl = data.url; // e.g. /api/upload/images/<file>
      setPreview(relativeUrl);
      onUpload(relativeUrl);
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg flex items-center gap-2 transition disabled:opacity-50"
        >
          <Upload size={18} />
          {uploading ? 'Uploading…' : label}
        </button>
        <input ref={fileInputRef} type="file" accept="image/*,.gif" onChange={handleUpload} className="hidden" />
        <span className="text-gray-500 text-sm">or paste URL below</span>
      </div>
      {preview && <img src={preview} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />}
    </div>
  );
};

// Reusable string-array editor (for bullet lists, features, paragraphs)
export const StringListEditor = ({ items = [], onChange, placeholder = '' , textarea = false}) => (
  <div>
    {items.map((val, idx) => (
      <div key={idx} className="flex gap-2 mb-2">
        {textarea ? (
          <textarea
            value={val}
            onChange={(e) => {
              const next = [...items]; next[idx] = e.target.value; onChange(next);
            }}
            className="flex-1 border rounded-lg px-3 py-2"
            rows={3}
            placeholder={placeholder}
          />
        ) : (
          <input
            type="text"
            value={val}
            onChange={(e) => {
              const next = [...items]; next[idx] = e.target.value; onChange(next);
            }}
            className="flex-1 border rounded-lg px-3 py-2"
            placeholder={placeholder}
          />
        )}
        <button onClick={() => onChange(items.filter((_, i) => i !== idx))} className="text-red-600 hover:bg-red-50 p-2 rounded">
          <Trash2 size={18} />
        </button>
      </div>
    ))}
    <button onClick={() => onChange([...items, ''])} className="text-[#C9A86C] hover:underline text-sm flex items-center gap-1">
      <Plus size={16} /> Add
    </button>
  </div>
);

// Toast helpers via callback (passed from parent)
export const useMessage = () => {
  const [message, setMessage] = useState({ type: '', text: '' });
  const show = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };
  return [message, show];
};
